import {
	QueryKey,
	UseQueryOptions,
	useMutation,
	useQueryClient
} from "@tanstack/react-query";

import { config } from "config";
import { Dictionary, isEmpty } from "lodash";

export interface APIRequest<Vars = {}, Data = {}> {
	endpoint: APIEndpoint<Vars>;
	baseUrl?: string;
	headers?: Dictionary<string>;
	method?: "GET" | "POST" | "PUT" | "DELETE";
	dataKey?: string;
	legacy?: boolean;
	body?: Vars;
	json?: boolean;
}

type APIEndpoint<Vars> = string | ((vars: Vars) => string);

export function useEmpirichMutation<T, Data = {}, Body = Data>(
	request: APIRequest<Data, Body>,
	queryKey: QueryKey,
	options?: UseQueryOptions<T> & {
		queriesToInvalidate?:
			| ((input: Data, response: T) => QueryKey[])
			| QueryKey[];
		shouldThrowError?: boolean;
	},
) {
	let getEndpoint = (data: Data) =>
		typeof request.endpoint === "string"
			? request.endpoint
			: request.endpoint(data);
	const queryClient = useQueryClient();
	return useMutation<T, {}, Data>(
		queryKey,
		async (data) => {
			let headers = {};
			return fetch(config().URL + getEndpoint(data), {
				headers,
				method: request.method ?? "POST",
				body:
					request.method !== "GET"
						? JSON.stringify(request.body ?? data)
						: null,
			})
				.then((res) => res.json())
				.then((res) => {
					if (res.hasError && options?.shouldThrowError) {
						throw new Error(res.error)	
					} 
					return request.dataKey != null ? res.payload[request.dataKey] : res.payload;
				},
				)
				.then((res) => {
					if (options?.queriesToInvalidate != null) {
						let queriesToInvalidate: QueryKey[] = [];
						if (typeof options.queriesToInvalidate === "function") {
							queriesToInvalidate = options.queriesToInvalidate(data, res);
						} else {
							queriesToInvalidate = options.queriesToInvalidate;
						}
						if (!isEmpty(queriesToInvalidate)) {
							// console.log("Invalidate", queriesToInvalidate);
							return Promise.all(
								queriesToInvalidate.map((d) =>
									queryClient.invalidateQueries(d),
								) ?? [],
							).then(() => res);
						}
					}

					return res;
				});
		},
		options as any,
	);
}
